/**
 * Document upload + synthetic generation. The mutate variables expose
 * ``{ sessionId, kind, ... }`` so components can read ``variables.kind``
 * to render per-kind loading state (e.g. a placeholder row in the rail).
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
    useGenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPost,
    useGetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost,
    useUploadDocumentApiSessionsSessionIdDocumentsPost,
} from '@/codegen/openapi/client';
import { QUERY_KEY } from '@/config/constants';
import { deferredInvalidateQueries } from './utils/deferredInvalidation';
import type { DocumentKind, ParsedDocument, AppealTypeSelection } from '@/api/types';

interface BundlePlanVariables {
    sessionId: string;
    /** Appeal-type selection the bundle is generated under. Falls back
     * to the session's locked selection when omitted. */
    selections?: AppealTypeSelection[];
}

/** Fetch the ordered document set the sample bundle should generate for
 * the chosen appeal type(s). Backend-owned so it can't drift from the
 * agent's required-docs map. Read-only — does not lock or generate. */
export function useBundlePlan() {
    const mutation = useGetBundlePlanApiSessionsSessionIdDocumentsBundlePlanPost();

    const fetchPlan = useCallback(
        async ({ sessionId, selections }: BundlePlanVariables): Promise<DocumentKind[]> => {
            const r = await mutation.mutateAsync({ sessionId, data: { selections } });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Bundle plan failed');
            return r.data.kinds;
        },
        [mutation],
    );

    return useMemo(
        () => ({ bundlePlan: fetchPlan, isPending: mutation.isPending }),
        [fetchPlan, mutation.isPending],
    );
}

interface GenerateSyntheticVariables {
    sessionId: string;
    kind: DocumentKind;
    /** Only sent on the first (session-locking) call; later docs inherit
     * the locked selection from the session. */
    selections?: AppealTypeSelection[];
}

export function useGenerateSynthetic() {
    const queryClient = useQueryClient();
    const mutation = useGenerateSyntheticApiSessionsSessionIdDocumentsSyntheticPost();

    const generate = useCallback(
        async ({
            sessionId,
            kind,
            selections,
        }: GenerateSyntheticVariables): Promise<ParsedDocument> => {
            const r = await mutation.mutateAsync({
                sessionId,
                data: { kind, selections },
            });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Generation failed');
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutation, queryClient],
    );

    // Expose mutation.variables.kind so the caller can render per-kind
    // pending rows. Useful when multiple kinds can be in flight (rare,
    // but the API allows it).
    return useMemo(
        () => ({
            generateSynthetic: generate,
            isPending: mutation.isPending,
            pendingKind: mutation.isPending
                ? (mutation.variables?.data.kind ?? null)
                : null,
        }),
        [generate, mutation.isPending, mutation.variables],
    );
}

interface UploadDocumentVariables {
    sessionId: string;
    kind: DocumentKind;
    file: File;
}

export function useUploadDocument() {
    const queryClient = useQueryClient();
    const mutation = useUploadDocumentApiSessionsSessionIdDocumentsPost();

    const upload = useCallback(
        async ({ sessionId, kind, file }: UploadDocumentVariables): Promise<ParsedDocument> => {
            const r = await mutation.mutateAsync({
                sessionId,
                // OpenAPI types ``file`` as string-binary; the runtime
                // hands the File straight to FormData.append, which is
                // what axios + FastAPI both want.
                data: { kind, file: file as unknown as string },
            });
            if (!r.data) throw new Error(r.error?.toString() ?? 'Upload failed');
            deferredInvalidateQueries(queryClient, [QUERY_KEY.SESSION, sessionId]);
            return r.data;
        },
        [mutation, queryClient],
    );

    return useMemo(
        () => ({
            uploadDocument: upload,
            isPending: mutation.isPending,
            pendingKind: mutation.isPending
                ? (mutation.variables?.data.kind ?? null)
                : null,
        }),
        [upload, mutation.isPending, mutation.variables],
    );
}
